import React, { createContext, useState, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import ParameterService from '../../../services/parameterService';

const Lp5ConfigurationContext = createContext();

export const useLp5Configuration = () => {
    return useContext(Lp5ConfigurationContext);
};

// Looks up (and lazily fetches) the promise config for an arbitrary lp5 value,
//  e.g. per table row in the case of showing multiple campaigns.
export const usePromiseConfigForLp5 = (targetLp5) => {
    const context = useLp5Configuration();
    const fetchConfigForLp5 = context?.fetchConfigForLp5;

    useEffect(() => {
        fetchConfigForLp5?.(targetLp5);
    }, [targetLp5, fetchConfigForLp5]);

    return targetLp5 ? context?.configCache?.[targetLp5] ?? null : null;
};

export const Lp5ConfigurationProvider = ({ lp5, children }) => {
    const [visibleFields, setVisibleFields] = useState({});
    const [promiseConfig, setPromiseConfig] = useState(null);
    const [configCache, setConfigCache] = useState({});
    const pendingLp5Requests = useRef(new Set());
    const parameterService = useMemo(() => new ParameterService(), []);

    // Fetches and caches the config for a given lp5, skipping if already cached or in flight.
    const fetchConfigForLp5 = useCallback((targetLp5) => {
        if (!targetLp5 || configCache[targetLp5] || pendingLp5Requests.current.has(targetLp5)) return;

        pendingLp5Requests.current.add(targetLp5);
        parameterService.getLp5Configuration(
            targetLp5,
            (data) => {
                pendingLp5Requests.current.delete(targetLp5);
                if (data && data.promiseConfig) {
                    setConfigCache((prev) => ({ ...prev, [targetLp5]: data.promiseConfig }));
                }
            },
            (error) => {
                pendingLp5Requests.current.delete(targetLp5);
                console.error("Error fetching lp5 configuration.", error);
            }
        );
    }, [configCache, parameterService]);

    useEffect(() => {
        if (!lp5) return;

        parameterService.getLp5Configuration(
            lp5,
            (data) => {
                if (data && data.visibleFields) {
                    setVisibleFields(data.visibleFields);
                }
                if (data && data.promiseConfig) {
                    setPromiseConfig(data.promiseConfig);
                    setConfigCache((prev) => ({ ...prev, [lp5]: data.promiseConfig }));
                }
            },
            (error) => {
                console.error("Error fetching lp5 configuration.", error);
            }
        );
    }, [lp5, parameterService]);

    return (
        <Lp5ConfigurationContext.Provider value={{ visibleFields, promiseConfig, configCache, fetchConfigForLp5 }}>
            {children}
        </Lp5ConfigurationContext.Provider>
    );
};
